import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomModal, CustomModalProps } from 'components/shared';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { NO_SELECTED_ORDERS_ERROR } from 'constants/validationErrors';
import { useForm } from 'hooks';
import { useModal } from 'hooks/useModal';
import { AddAdditionalOrdersFormValues, AdditionalOrder } from 'interfaces';
import { HTTPService } from 'service';
import { countOrdersTotalPalletsAmount, findOrdersByIds, getPalletLabel } from 'utils/functions';
import { logNetworkError } from 'utils/logNetworkError';
import { addAdditionalOrdersFormValidation } from 'utils/validation/addAdditionalOrdersFormValidation';

import AdditionalOrdersList from './AdditionalOrdersList/AdditionalOrdersList';
import styles from './AddFreeOrdersToNextOrderModal.module.css';

type AddFreeOrdersToNextOrderModalProps = CustomModalProps & {
   availableAdditionalOrders: AdditionalOrder[];
   parentOrderId: string;
   onSuccess?: () => void;
};

const INITIAL_FORM_STATE: AddAdditionalOrdersFormValues = {
   selectedAdditionalOrderIds: [],
};

const AddFreeOrdersToNextOrderModal = ({
   availableAdditionalOrders,
   parentOrderId,
   onSuccess,
   onClose,
   ...props
}: AddFreeOrdersToNextOrderModalProps) => {
   const { t } = useTranslation();
   const { isLoading, errorInfo, setIsLoading, setErrorInfo, closeModalHandler } = useModal({
      onClose,
   });

   const { DEFAULT_ERROR_DESCRIPTIONS } = useErrorDescriptions();

   const handleAddAdditionaOrders = () => {
      setIsLoading(true);
      HTTPService.mergeAdditionalOrders(parentOrderId, selectedAdditionalOrderIds)
         .then(() => (onSuccess ? onSuccess() : closeModalHandler()))
         .catch(error => {
            setErrorInfo(DEFAULT_ERROR_DESCRIPTIONS.COMPLETE_ACTION_SHORT);
            logNetworkError(error);
         })
         .finally(() => setIsLoading(false));
   };

   const { values, touched, formValid, multiSelectChangeHandler, submitHandler } = useForm({
      initialState: INITIAL_FORM_STATE,
      validations: addAdditionalOrdersFormValidation,
      onSubmit: handleAddAdditionaOrders,
   });

   const handleSelectAdditionalOrder = (orderId: string) =>
      multiSelectChangeHandler(orderId, 'selectedAdditionalOrderIds');

   const selectedAdditionalOrderIds = values.selectedAdditionalOrderIds;

   const selectedPalletsAmount = useMemo(() => {
      if (availableAdditionalOrders) {
         const foundSelectedAdditionalOrders = findOrdersByIds(
            availableAdditionalOrders,
            selectedAdditionalOrderIds as string[],
         );
         return countOrdersTotalPalletsAmount(foundSelectedAdditionalOrders);
      } else {
         return 0;
      }
   }, [selectedAdditionalOrderIds, availableAdditionalOrders]);

   const isFormInvalid = !formValid;
   const isAnyAdditionalOrderTouched = touched.selectedAdditionalOrderIds;
   const modalErrorMessage =
      isFormInvalid && isAnyAdditionalOrderTouched ? NO_SELECTED_ORDERS_ERROR() : errorInfo;

   return (
      <CustomModal
         {...props}
         title={t('add_free_orders_modal.title')}
         error={modalErrorMessage}
         modalContentClassName={styles.modalContent}
         leftSideModalContent
         isLoading={isLoading}
         onSubmit={submitHandler}
         onClose={closeModalHandler}
         primaryButtonProps={{
            text: t('add_free_orders_modal.buttons.attach'),
            variant: 'filled-primary',
         }}
         secondaryButtonProps={{
            text: t('add_free_orders_modal.buttons.skip'),
            variant: 'text',
            onClick: closeModalHandler,
         }}
      >
         <>
            <p className={styles.description}>
               {t('add_free_orders_modal.description.line1')}
               <br />
               {t('add_free_orders_modal.description.line2')}
            </p>
            {availableAdditionalOrders && (
               <AdditionalOrdersList
                  additionalOrders={availableAdditionalOrders}
                  selectedAdditionalOrderIds={selectedAdditionalOrderIds}
                  isAnyAdditionalOrderTouched={isAnyAdditionalOrderTouched}
                  isFormInvalid={isFormInvalid}
                  onSelectAdditionalOrder={handleSelectAdditionalOrder}
               />
            )}
            <div className={styles.selectedPalletsAmout}>
               <span>{t('add_free_orders_modal.selected.label')}</span>
               <span>{`${selectedPalletsAmount} ${getPalletLabel(selectedPalletsAmount)}`}</span>
            </div>
         </>
      </CustomModal>
   );
};

export default AddFreeOrdersToNextOrderModal;
